import React, { useState, useEffect } from 'react';
import { FaSearch, FaCheckCircle, FaTimesCircle } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../SidebarInAdminPanel/Sidebar';
import { collection, getDocs } from 'firebase/firestore';
import { firestore } from '../../backend/firebaseConfig';

const UserManagementPage = () => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('all');
  const navigate = useNavigate();

  useEffect(() => {
    const fetchUsers = async () => {
      const querySnapshot = await getDocs(collection(firestore, 'users'));
      const usersArray = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setUsers(usersArray);
      setFilteredUsers(usersArray);
    };
    fetchUsers();
  }, []);

  const getVerificationStatusIcon = (isverified) => {
    return isverified ? <FaCheckCircle className="text-green-500" /> : <FaTimesCircle className="text-red-500" />;
  };

  const handleFilterChange = (filterType) => {
    setFilter(filterType);
    switch (filterType) {
      case 'verified':
        setFilteredUsers(users.filter(user => user.isverified));
        break;
      case 'unverified':
        setFilteredUsers(users.filter(user => !user.isverified));
        break;
      case 'user':
        setFilteredUsers(users.filter(user => user.userType === 'User'));
        break;
      case 'driver':
        setFilteredUsers(users.filter(user => user.userType === 'Driver'));
        break;
      default:
        setFilteredUsers(users);
        break;
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    const searchValue = e.target.value.toLowerCase();
    if (searchValue) {
      setFilteredUsers(
        users.filter(user => 
          (user.email && user.email.toLowerCase().includes(searchValue)) ||
          (user.cnic && user.cnic.toLowerCase().includes(searchValue)) || 
          (user.fullname && user.fullname.toLowerCase().includes(searchValue))
        )
      );
    } else {
      handleFilterChange(filter);
    }
  };

  return (
    <div className="flex flex-col md:flex-row bg-gradient-to-r from-[#0AD1C8] to-[#073235]">
      <Sidebar />
      <div className="flex-1 p-8">
        <h1 className="text-4xl font-bold mb-8 text-white">User Management Dashboard</h1>

        {/* Stats Bar - Responsive */}
        <div className="bg-white p-6 rounded-xl shadow-md mb-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <button className="bg-blue-100 p-4 text-left rounded-lg focus:outline-none" onClick={() => handleFilterChange('all')}>
              <h3 className="text-lg font-semibold text-blue-800">Total Users</h3>
              <p className="text-3xl font-bold text-blue-600">{users.length}</p>
            </button>
            <button className="bg-green-100 p-4 text-left rounded-lg focus:outline-none" onClick={() => handleFilterChange('verified')}>
              <h3 className="text-lg font-semibold text-green-800">Verified Users</h3>
              <p className="text-3xl font-bold text-green-600">{users.filter(user => user.isverified).length}</p>
            </button>
            <button className="bg-red-100 p-4 text-left rounded-lg focus:outline-none" onClick={() => handleFilterChange('unverified')}>
              <h3 className="text-lg font-semibold text-red-800">Unverified Users</h3>
              <p className="text-3xl font-bold text-red-600">{users.filter(user => !user.isverified).length}</p>
            </button>
          </div>

          {/* Search & Filters */}
          <div className="flex flex-col md:flex-row justify-between items-center mb-6">
            <div className="relative w-full md:w-1/2 mb-4 md:mb-0">
              <input
                type="text"
                placeholder="Search by email, CNIC or Full Name"
                className="w-full pl-10 pr-4 py-2 border rounded-full focus:ring-2 focus:ring-blue-500"
                value={searchTerm}
                onChange={handleSearch}
              />
              <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            </div>
            <div className="flex items-center gap-4">
              <button onClick={() => handleFilterChange('all')} className={`px-4 py-2 rounded-full ${filter === 'all' ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}>All</button>
              <button onClick={() => handleFilterChange('user')} className={`px-4 py-2 rounded-full ${filter === 'user' ? 'bg-green-500 text-white' : 'bg-gray-200'}`}>User</button>
              <button onClick={() => handleFilterChange('driver')} className={`px-4 py-2 rounded-full ${filter === 'driver' ? 'bg-red-500 text-white' : 'bg-gray-200'}`}>Driver</button>
            </div>
          </div>

          {/* Users Table - Responsive */}
          <div className="overflow-x-auto bg-white rounded-xl shadow-inner">
            <div className="max-h-96 overflow-y-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr className="hidden md:table-row">
                  <th className="p-3 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">ID</th>
                    <th className="p-3 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">Full Name</th>
                    <th className="p-3 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">Email</th>
                    <th className="p-3 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">Phone</th>
                    <th className="p-3 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">CNIC</th>
                    <th className="p-3 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">User Type</th>
                    <th className="p-3 text-left text-sm font-medium text-gray-500 uppercase tracking-wider">Verified</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredUsers.map(user => (
                   <tr key={user.id} 
                   className={`hover:bg-opacity-75 cursor-pointer ${user.isverified ? 'bg-green-100' : 'bg-red-100'}`} 
                   onClick={() => navigate(`/user-detail/${user.id}`)}>
                      <td className="p-3 text-gray-900 md:table-cell hidden">{user.id}</td>
                      <td className="p-3 text-gray-900">{user.fullname}</td>
                      <td className="p-3 text-gray-900 md:table-cell hidden">{user.email}</td>
                      <td className="p-3 text-gray-900 md:table-cell hidden">{user.phone}</td>
                      <td className="p-3 text-gray-900 md:table-cell hidden">{user.cnic}</td>
                      <td className="p-3 text-gray-900">{user.userType}</td>
                      <td className="p-3 text-gray-900 flex items-center justify-center">{getVerificationStatusIcon(user.isverified)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserManagementPage;
